<template>
  <div class="games-activity">
    <div class="activity-header">
      <div class="header-left">
        <h2>{{ $t('games.yourGames') }}</h2>
        <div class="games-info">
          {{ $t('games.showingRange', { start: paginationInfo.start, end: paginationInfo.end, total: paginationInfo.total }) }}
        </div>
      </div>
      <div class="header-controls">
        <div class="sort-controls">
          <label for="sort-select">{{ $t('games.sortBy') }}</label>
          <select
            id="sort-select"
            :value="sortBy"
            @change="onSortSelect($event.target.value)"
          >
            <option value="daily_playtime">{{ $t('games.playtime') }}</option>
            <option value="name">{{ $t('games.gameName') }}</option>
            <option value="recent">{{ $t('games.lastPlayed') }}</option>
            <option value="days">{{ $t('games.daysPlayed') }}</option>
          </select>
          <button type="button" class="sort-btn" @click="$emit('toggle-sort-order')">
            {{ sortOrder === 'desc' ? '↓' : '↑' }}
          </button>
        </div>
        <div class="items-per-page">
          <label for="games-per-page">{{ $t('games.show') }}</label>
          <select
            id="games-per-page"
            :value="gamesPerPage"
            @change="onGamesPerPageChange($event.target.value)"
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
        </div>
      </div>
    </div>

    <div class="games-table">
      <table>
        <thead>
          <tr>
            <th @click="$emit('set-sort-by', 'name')" class="sortable">
              {{ $t('games.gameName') }}
              <span v-if="sortBy === 'name'" class="sort-indicator">{{ sortOrder === 'desc' ? '↓' : '↑' }}</span>
            </th>
            <th @click="$emit('set-sort-by', 'daily_playtime')" class="sortable">
              {{ $t('games.playtime') }}
              <span v-if="sortBy === 'daily_playtime'" class="sort-indicator">{{ sortOrder === 'desc' ? '↓' : '↑' }}</span>
            </th>
            <th @click="$emit('set-sort-by', 'days')" class="sortable">
              {{ $t('games.daysPlayed') }}
              <span v-if="sortBy === 'days'" class="sort-indicator">{{ sortOrder === 'desc' ? '↓' : '↑' }}</span>
            </th>
            <th @click="$emit('set-sort-by', 'recent')" class="sortable">
              {{ $t('games.lastActivity') }}
              <span v-if="sortBy === 'recent'" class="sort-indicator">{{ sortOrder === 'desc' ? '↓' : '↑' }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="game in games" :key="game.app_id" class="game-row">
            <td class="game-name" :data-label="$t('games.gameName')">
              <div class="game-info">
                <div class="game-image">
                  <a :href="getSteamStoreUrl(game.app_id)" target="_blank" rel="noopener noreferrer">
                    <img
                      :src="getGameImageUrl(game.app_id)"
                      :alt="getGameName(game.app_id)"
                      @error="$event.target.style.display='none'"
                      loading="lazy"
                    >
                  </a>
                </div>
                <div class="game-text">
                  <strong class="game-title">{{ getGameName(game.app_id) }}</strong>
                  <div v-if="achForApp(game.app_id)" class="game-achievements">
                    <span
                      v-for="(ic, i) in achForApp(game.app_id).icons"
                      :key="i"
                      class="game-ach-icon"
                      :data-name="ic.name || ''"
                    >
                      <img
                        :src="achievementIconUrl(ic.icon)"
                        :alt="ic.name || 'Achievement'"
                        loading="lazy"
                        @error="$event.target.style.display='none'"
                      >
                    </span>
                    <span class="game-ach-count">{{ $t('games.unlockedCount', { count: achForApp(game.app_id).count }) }}</span>
                  </div>
                </div>
              </div>
            </td>
            <td class="playtime" :data-label="$t('games.playtime')">
              <strong>{{ formatPlaytime(displayPlaytime(game)) }}</strong>
            </td>
            <td class="days-played" :data-label="$t('games.daysPlayed')">{{ game.days_played }}</td>
            <td class="last-played" :data-label="$t('games.lastActivity')">
              {{ game.last_activity_date ? formatDate(game.last_activity_date, { fallback: $t('common.never'), includeTime: false }) : $t('common.never') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pagination-container" v-if="totalPages > 1">
      <div class="pagination">
        <button
          type="button"
          class="page-btn"
          :title="$t('games.firstPage')"
          :disabled="currentPage === 1"
          @click="$emit('go-to-page', 1)"
        >
          ⟪
        </button>

        <button
          type="button"
          class="page-btn"
          :title="$t('games.prevPage')"
          :disabled="currentPage === 1"
          @click="$emit('go-to-page', currentPage - 1)"
        >
          ‹
        </button>

        <div class="page-numbers">
          <button
            v-for="page in visiblePages"
            :key="page"
            type="button"
            class="page-btn"
            :class="{ active: page === currentPage }"
            @click="$emit('go-to-page', page)"
          >
            {{ page }}
          </button>
        </div>

        <button
          type="button"
          class="page-btn"
          :title="$t('games.nextPage')"
          :disabled="currentPage === totalPages"
          @click="$emit('go-to-page', currentPage + 1)"
        >
          ›
        </button>

        <button
          type="button"
          class="page-btn"
          :title="$t('games.lastPage')"
          :disabled="currentPage === totalPages"
          @click="$emit('go-to-page', totalPages)"
        >
          ⟫
        </button>
      </div>

      <div class="pagination-info">
        {{ $t('games.pageOf', { page: currentPage, total: totalPages }) }}
      </div>
    </div>
  </div>
</template>

<script>
import { achievementIconUrl } from '../../utils/achievements'

export default {
  name: 'GamesActivitySection',
  props: {
    games: {
      type: Array,
      default: () => []
    },
    paginationInfo: {
      type: Object,
      default: () => ({ start: 0, end: 0, total: 0 })
    },
    sortBy: {
      type: String,
      default: 'recent'
    },
    sortOrder: {
      type: String,
      default: 'desc'
    },
    gamesPerPage: {
      type: Number,
      default: 5
    },
    totalPages: {
      type: Number,
      default: 0
    },
    currentPage: {
      type: Number,
      default: 1
    },
    visiblePages: {
      type: Array,
      default: () => []
    },
    formatPlaytime: {
      type: Function,
      required: true
    },
    displayPlaytime: {
      type: Function,
      required: true
    },
    formatDate: {
      type: Function,
      required: true
    },
    getGameName: {
      type: Function,
      required: true
    },
    getGameImageUrl: {
      type: Function,
      required: true
    },
    getSteamStoreUrl: {
      type: Function,
      required: true
    },
    // Per-game achievement strips { appId: { count, icons:[{name,icon}] } }.
    achievementsByApp: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['update:sortBy', 'toggle-sort-order', 'update:gamesPerPage', 'sort-games', 'go-to-page', 'set-sort-by'],
  methods: {
    achievementIconUrl,
    achForApp(appId) {
      return this.achievementsByApp[appId] || this.achievementsByApp[String(appId)] || null
    },
    onSortSelect(value) {
      this.$emit('update:sortBy', value)
      this.$emit('sort-games')
    },
    onGamesPerPageChange(value) {
      const parsed = Number(value)
      this.$emit('update:gamesPerPage', Number.isFinite(parsed) ? parsed : this.gamesPerPage)
    }
  }
}
</script>

<style scoped>
.games-activity {
  background: var(--panel);
  border: 1px solid var(--panel-border);
  border-radius: 8px;
  padding: 16px;
}

.activity-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
}

.header-left h2 {
  margin: 0 0 5px 0;
  color: var(--text);
}

.games-info {
  color: var(--muted);
  font-size: 0.9rem;
}

.header-controls {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.sort-controls,
.items-per-page {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sort-controls label,
.items-per-page label {
  color: var(--muted);
  font-size: 0.9rem;
}

.sort-controls select,
.items-per-page select {
  padding: 6px 10px;
  border: 1px solid var(--panel-border);
  border-radius: 6px;
  background: #0f141a;
  color: var(--text);
  font-size: 0.9rem;
}

.sort-btn {
  background: var(--panel);
  border: 1px solid var(--panel-border);
  color: var(--text);
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.sort-btn:hover {
  background: #0f141a;
  border-color: var(--primary);
  color: var(--primary);
}

.games-table table {
  width: 100%;
  border-collapse: collapse;
}

.games-table th,
.games-table td {
  padding: 12px 10px;
  text-align: left;
  border-bottom: 1px solid var(--panel-border);
}

.games-table th {
  color: var(--muted);
  font-weight: 600;
  cursor: pointer;
}

.sortable {
  position: relative;
}

.sort-indicator {
  margin-left: 4px;
  font-size: 0.85rem;
}

.game-row:hover {
  background: #0f141a;
}

.game-name {
  min-width: 300px;
}

.game-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.game-image {
  flex-shrink: 0;
}

.game-image img {
  width: 80px;
  height: 37px;
  object-fit: cover;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.game-text strong {
  display: block;
  color: var(--text);
  margin-bottom: 2px;
  font-size: 1.15rem;
}

.game-achievements {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
}

.game-ach-icon {
  display: inline-flex;
  line-height: 0;
  position: relative;
}

/* Instant tooltip — the native title attribute appears after ~1s and never
   on touch, which read as "no hover at all". */
.game-ach-icon:not([data-name='']):hover::after,
.game-ach-icon:not([data-name='']):focus-within::after {
  content: attr(data-name);
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: rgba(13, 18, 26, 0.97);
  color: var(--text);
  border: 1px solid var(--panel-border);
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  z-index: 30;
  pointer-events: none;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.35);
}

.game-ach-icon img {
  width: 24px;
  height: 24px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid var(--panel-border);
}

.game-ach-count {
  margin-left: 4px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--muted);
  white-space: nowrap;
}

.playtime strong {
  display: block;
  color: var(--primary);
}

.playtime small {
  color: var(--muted);
  font-size: 0.8rem;
}

.days-played {
  text-align: center;
  font-weight: 500;
}

.last-played {
  font-size: 0.85rem;
  color: var(--muted);
}

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--panel-border);
  flex-wrap: wrap;
  gap: 15px;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 5px;
}

.page-numbers {
  display: flex;
  gap: 2px;
}

.page-btn {
  background: var(--panel);
  border: 1px solid var(--panel-border);
  color: var(--muted);
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  min-width: 40px;
  text-align: center;
}

.page-btn:hover:not(:disabled) {
  background: #0f141a;
  border-color: var(--primary);
  color: var(--primary);
}

.page-btn:disabled {
  background: #0f141a;
  color: #555;
  cursor: not-allowed;
  border-color: #222;
}

.page-btn.active {
  background: var(--primary);
  color: #0b0e12;
  border-color: var(--primary);
}

.pagination-info {
  color: var(--muted);
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .games-table thead {
    display: none;
  }

  .games-table table {
    display: block;
    border: 0;
  }

  .games-table tbody {
    display: block;
  }

  .games-table tr {
    display: block;
    background: var(--panel);
    border: 1px solid var(--panel-border);
    border-radius: 10px;
    margin-bottom: 10px;
    padding: 10px;
  }

  .games-table td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 6px;
    border: none;
  }

  .games-table td::before {
    content: attr(data-label);
    color: var(--muted);
    font-weight: 600;
    margin-right: 10px;
  }

  .games-table td.game-name {
    display: block;
    padding-top: 0;
    /* Desktop sets min-width:300px for the table column; on the mobile
       card layout that min-content propagates up and overflows the page. */
    min-width: 0;
  }

  .games-table td.game-name::before {
    content: none;
  }

  .game-info {
    align-items: flex-start;
  }

  .game-image img {
    width: 100%;
    max-width: 160px;
    height: auto;
  }

  /* The pagination is a non-wrapping flex row of page buttons; with enough
     pages its min-content is wider than the viewport and shifts the page
     right. Let it wrap (and centre) on narrow screens instead. */
  .pagination,
  .page-numbers {
    flex-wrap: wrap;
    justify-content: center;
  }
}
</style>
